import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Megaphone, Plus, Calendar, Edit, Trash2, Eye, EyeOff,
  Loader2, AlertTriangle, Gift, Bell, Info, ExternalLink,
  Users, Briefcase, UserCheck, Globe, Palette, Image
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';


const ClientNewsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data
  const [newsList, setNewsList] = useState([]);
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [newsType, setNewsType] = useState('info');
  const [active, setActive] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  
  // New fields
  const [targetAudience, setTargetAudience] = useState('clients');
  const [bgColor, setBgColor] = useState('');
  const [bgImageUrl, setBgImageUrl] = useState('');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await api.get('/client-news/');
      setNewsList(response.data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Erreur lors du chargement des actualités');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (news = null) => {
    if (news) {
      setEditingNews(news);
      setTitle(news.title || '');
      setContent(news.content || '');
      setLinkUrl(news.link_url || '');
      setLinkText(news.link_text || '');
      setNewsType(news.type || 'info');
      setActive(news.active !== false);
      setStartDate(news.start_date ? news.start_date.split('T')[0] : '');
      setEndDate(news.end_date ? news.end_date.split('T')[0] : '');
      setTargetAudience(news.target_audience || 'clients');
      setBgColor(news.bg_color || '');
      setBgImageUrl(news.bg_image_url || '');
    } else {
      setEditingNews(null);
      setTitle('');
      setContent('');
      setLinkUrl('');
      setLinkText('');
      setNewsType('info');
      setActive(true);
      setStartDate('');
      setEndDate('');
      setTargetAudience('clients');
      setBgColor('');
      setBgImageUrl('');
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!title || !content) {
      toast.error('Titre et contenu sont obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title,
        content,
        link_url: linkUrl || null,
        link_text: linkText || null,
        type: newsType,
        active,
        start_date: startDate ? new Date(startDate).toISOString() : null,
        end_date: endDate ? new Date(endDate).toISOString() : null,
        target_audience: targetAudience,
        bg_color: bgColor || null,
        bg_image_url: bgImageUrl || null
      };
      
      if (editingNews) {
        await axios.put(`${API}/client-news/${editingNews.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Actualité mise à jour');
      } else {
        await axios.post(`${API}/client-news/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Actualité créée');
      }
      
      setShowModal(false);
      fetchNews();
    } catch (error) {
      console.error('Error saving news:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (newsId) => {
    if (!confirm('Supprimer cette actualité ?')) return;
    
    try {
      await api.delete('/client-news/${newsId}');
      toast.success('Actualité supprimée');
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (news) => {
    try {
      await axios.put(`${API}/client-news/${news.id}`, {
        ...news,
        active: !news.active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(news.active ? 'Actualité désactivée' : 'Actualité activée');
      fetchNews();
    } catch (error) {
      console.error('Error toggling news:', error);
      toast.error('Erreur');
    }
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'warning':
        return { label: 'Avertissement', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle };
      case 'promo':
        return { label: 'Promotion', color: 'bg-purple-100 text-purple-700', icon: Gift };
      case 'update':
        return { label: 'Mise à jour', color: 'bg-blue-100 text-blue-700', icon: Bell };
      default:
        return { label: 'Information', color: 'bg-emerald-100 text-emerald-700', icon: Info };
    }
  };

  const getAudienceConfig = (audience) => {
    switch (audience) {
      case 'members':
        return { label: 'Membres', color: 'bg-indigo-100 text-indigo-700', icon: Users };
      case 'subcontractors':
        return { label: 'Sous-traitants', color: 'bg-orange-100 text-orange-700', icon: Briefcase };
      case 'clients':
        return { label: 'Clients', color: 'bg-cyan-100 text-cyan-700', icon: UserCheck };
      case 'all':
        return { label: 'Tout le monde', color: 'bg-slate-100 text-slate-700', icon: Globe };
      default:
        return { label: 'Clients', color: 'bg-cyan-100 text-cyan-700', icon: UserCheck };
    }
  };

  // Predefined colors for quick selection
  const predefinedColors = [
    { name: 'Émeraude', value: '#10b981' },
    { name: 'Bleu', value: '#3b82f6' },
    { name: 'Violet', value: '#8b5cf6' },
    { name: 'Rose', value: '#ec4899' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Rouge', value: '#ef4444' },
    { name: 'Jaune', value: '#eab308' },
    { name: 'Cyan', value: '#06b6d4' },
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Noir', value: '#1e293b' },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8 pt-32">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Bandeau Actualités
              </h1>
              <p className="text-slate-500 mt-1">
                Gérez les actualités affichées pour tous les utilisateurs
              </p>
            </div>
            <Button onClick={() => openModal()} style={{ backgroundColor: theme.colors?.accent }}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle actualité
            </Button>
          </div>

          {/* News List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : newsList.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune actualité</h3>
                <p className="text-slate-500">Créez votre première actualité</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {newsList.map((news) => {
                const typeConfig = getTypeConfig(news.type);
                const audienceConfig = getAudienceConfig(news.target_audience);
                const IconComponent = typeConfig.icon;
                const AudienceIcon = audienceConfig.icon;
                const isExpired = news.end_date && new Date(news.end_date) < new Date();
                const notStarted = news.start_date && new Date(news.start_date) > new Date();
                
                return (
                  <Card key={news.id} className={cn(
                    "transition-all",
                    !news.active && "opacity-60"
                  )}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className={typeConfig.color}>
                              <IconComponent className="w-3 h-3 mr-1" />
                              {typeConfig.label}
                            </Badge>
                            <Badge className={audienceConfig.color}>
                              <AudienceIcon className="w-3 h-3 mr-1" />
                              {audienceConfig.label}
                            </Badge>
                            {!news.active && (
                              <Badge variant="outline" className="text-slate-500">
                                <EyeOff className="w-3 h-3 mr-1" />
                                Inactif
                              </Badge>
                            )}
                            {isExpired && (
                              <Badge variant="outline" className="text-red-500">Expiré</Badge>
                            )}
                            {notStarted && (
                              <Badge variant="outline" className="text-blue-500">Programmé</Badge>
                            )}
                            {news.bg_color && (
                              <div 
                                className="w-5 h-5 rounded-full border-2 border-white shadow-sm" 
                                style={{ backgroundColor: news.bg_color }}
                                title={`Couleur: ${news.bg_color}`}
                              />
                            )}
                            {news.bg_image_url && (
                              <Badge variant="outline" className="text-slate-500">
                                <Image className="w-3 h-3 mr-1" />
                                Image
                              </Badge>
                            )}
                          </div>
                          
                          <h3 className="font-semibold text-lg text-slate-800">{news.title}</h3>
                          <p className="text-slate-600 mt-1">{news.content}</p>
                          
                          {news.link_url && (
                            <a 
                              href={news.link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-2"
                            >
                              {news.link_text || news.link_url}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          
                          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                            {news.start_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Du {new Date(news.start_date).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                            {news.end_date && (
                              <span>au {new Date(news.end_date).toLocaleDateString('fr-FR')}</span>
                            )}
                            {news.created_by_name && (
                              <span>Créé par {news.created_by_name}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleActive(news)}
                            title={news.active ? 'Désactiver' : 'Activer'}
                          >
                            {news.active ? (
                              <Eye className="w-4 h-4 text-emerald-500" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-slate-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openModal(news)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(news.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5" />
              {editingNews ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
            </DialogTitle>
            <DialogDescription>
              Cette actualité sera affichée dans le bandeau en haut de la plateforme
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Type and Active */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={newsType} onValueChange={setNewsType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Information</SelectItem>
                    <SelectItem value="update">Mise à jour</SelectItem>
                    <SelectItem value="promo">Promotion</SelectItem>
                    <SelectItem value="warning">Avertissement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Audience cible *</Label>
                <Select value={targetAudience} onValueChange={setTargetAudience}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Tout le monde
                      </div>
                    </SelectItem>
                    <SelectItem value="clients">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4" />
                        Clients uniquement
                      </div>
                    </SelectItem>
                    <SelectItem value="members">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Membres de l&apos;équipe
                      </div>
                    </SelectItem>
                    <SelectItem value="subcontractors">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Sous-traitants uniquement
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Nouvelle fonctionnalité disponible"
              />
            </div>
            
            {/* Content */}
            <div className="space-y-2">
              <Label>Contenu *</Label>
              <Textarea 
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Le message à afficher..."
                rows={2}
              />
            </div>
            
            {/* Link */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lien URL (optionnel)</Label>
                <Input 
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label>Texte du lien</Label>
                <Input 
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="En savoir plus"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input 
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input 
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {/* Style section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Personnalisation du style
              </h4>
              
              {/* Background color */}
              <div className="space-y-2 mb-4">
                <Label>Couleur de fond</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {predefinedColors.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setBgColor(color.value)}
                      className={cn(
                        "w-8 h-8 rounded-full border-2 transition-all hover:scale-110",
                        bgColor === color.value ? "border-slate-800 ring-2 ring-offset-2 ring-slate-300" : "border-white shadow-sm"
                      )}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                  <Input 
                    type="color"
                    value={bgColor || '#10b981'}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-10 h-8 p-0.5 cursor-pointer"
                    title="Couleur personnalisée"
                  />
                  {bgColor && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setBgColor('')}
                      className="text-xs text-slate-500"
                    >
                      Réinitialiser
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500">Laissez vide pour utiliser la couleur par défaut du type sélectionné</p>
              </div>

              {/* Background image */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Image de fond (URL)
                </Label>
                <Input 
                  value={bgImageUrl}
                  onChange={(e) => setBgImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-slate-500">L&apos;image sera affichée en arrière-plan avec un overlay sombre</p>
                
                {bgImageUrl && (
                  <div className="relative h-16 rounded-lg overflow-hidden mt-2">
                    <img 
                      src={bgImageUrl} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-sm font-medium">Aperçu de l&apos;image</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between border-t pt-4 mt-4">
              <div>
                <Label>Activer immédiatement</Label>
                <p className="text-xs text-slate-500">L&apos;actualité sera visible dès maintenant</p>
              </div>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>

            {/* Preview */}
            {title && (
              <div className="border-t pt-4 mt-4">
                <Label className="mb-2 block">Aperçu</Label>
                <div 
                  className={cn(
                    "w-full py-2 px-4 rounded-lg text-white",
                    !bgColor && newsType === 'info' && "bg-gradient-to-r from-emerald-500 to-teal-500",
                    !bgColor && newsType === 'warning' && "bg-amber-500",
                    !bgColor && newsType === 'promo' && "bg-gradient-to-r from-purple-500 to-pink-500",
                    !bgColor && newsType === 'update' && "bg-blue-500"
                  )}
                  style={{
                    backgroundColor: bgColor || undefined,
                    backgroundImage: bgImageUrl ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${bgImageUrl})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Megaphone className="w-4 h-4" />
                    <span className="font-semibold">{title}</span>
                    {content && <span className="text-sm opacity-90">- {content}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingNews ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientNewsPage;
